import React, { createContext, PropsWithChildren, useEffect, useReducer } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { getLiteSession } from '@monorepo/shared/src/api/lite-session-student';
import { AppNames } from '@monorepo/shared/src/types';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import useLightRouteActivityStore from 'graph/state/query-hooks/use-light-route-activity-store';
import useAttemptStore from '@monorepo/shared/src/graph/state/query-hooks/use-attempt-store';
import WithQuery from 'components/layout/WithQuery';
import NextButton from '@monorepo/shared/src/components/common/Buttons/NextButton';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import { defaultState, reducers } from './reducers';
import { ActivityActions, ActivityActionTypes, ActivityState } from './types';
import { ActivityTypes, ButtonSize, AppRoutes } from '../../../types';
import { AttemptStatus } from '@monorepo/shared/src/graph/schema-generated-types-student';
import { formatCharacter } from '@monorepo/shared/src/util/telemetry-util';

const liteRouteActivityTypeStoreMap = {
  [ActivityTypes.MATCH_IT_TRASH_IT]: useLightRouteActivityStore,
  [ActivityTypes.STARTING_SOUND]: useLightRouteActivityStore,
  [ActivityTypes.WDT]: useLightRouteActivityStore,
};

type Props = {
  activity: string;
  appName: AppNames;
};

export const LiteRouteActivityContext = createContext<any>([]);

export const LiteRouteActivityProvider: React.FC<PropsWithChildren<Props>> = ({
  activity,
  appName,
  children,
}) => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_ACTIVITY);
  const [state, dispatch] = useReducer<React.Reducer<ActivityState, ActivityActions>>(
    reducers,
    defaultState()
  );
  const [, { handleSaveAttempt }] = useAttemptStore();

  const navigate = useNavigate();
  const { search } = useLocation();
  const { assignmentActivityId } = useParams();

  const query = new URLSearchParams(search);
  const bookCode = query.get('bookCode');

  const [activityData] = liteRouteActivityTypeStoreMap[activity](activity, bookCode);

  useEffect(() => {
    if (activityData?.activity?.book) {
      const { characters } = activityData.activity.book;
      fireEvent({
        activityType: activity,
        bookCode,
        character: formatCharacter(characters[0]),
      });
    }
  }, [activityData?.activity?.book]);

  const buildLiteRoute = async (activityUrl: string) => {
    const { sessionId } = await getLiteSession(appName);
    return `${activityUrl}&session=${sessionId}`;
  };

  const setActivityData = async (activityData) => {
    const activityUrl = await buildLiteRoute(activityData?.url);

    dispatch({
      type: ActivityActionTypes.SET_ACTIVITY,
      payload: { ...activityData, url: activityUrl },
    });
  };

  const setShowAffirmation = (showAffirmation: boolean) => {
    dispatch({
      type: ActivityActionTypes.SET_SHOW_AFFIRMATION,
      payload: showAffirmation,
    });
  };

  const handleEndOfActivity = () => {
    setShowAffirmation(true);
  };

  useEffect(() => {
    if (activityData.isLoading) return;
    setActivityData(activityData.activity);
  }, [activityData.isLoading]);

  useEffect(() => {
    assignmentActivityId &&
      handleSaveAttempt({
        assignmentActivityId: JSON.parse(assignmentActivityId),
        status: AttemptStatus.IN_PROGRESS,
        dateStarted: new Date(),
      });
  }, [assignmentActivityId]);

  useEffect(() => {
    if (!state.showAffirmation || !assignmentActivityId) return;
    navigate({
      pathname: `/${AppRoutes.ACTIVITY_COMPLETE}/${assignmentActivityId}`,
      search,
    });
  }, [state.showAffirmation]);

  return (
    <LiteRouteActivityContext.Provider value={[state, { handleEndOfActivity }]}>
      <WithQuery loading={activityData.isLoading} error={activityData.error}>
        {children}
        {!state.showAffirmation && (
          <NextButton onClick={handleEndOfActivity} size={ButtonSize.SMALL} pulsing />
        )}
      </WithQuery>
    </LiteRouteActivityContext.Provider>
  );
};

export default LiteRouteActivityProvider;
